"use client";

import { useState, useEffect, useMemo } from "react";
import toast from "react-hot-toast";
import { Loader2 } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "./ui/dialog";

const formatCurrency = (value) =>
  parseFloat(value || 0).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

export default function TokenReserveWithdrawModal({
  isOpen,
  onClose,
  currentBalance,
  onWithdraw,
}) {
  const [amount, setAmount] = useState("");
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (isOpen) {
      setAmount("");
      setMessage("");
      setError(null);
      setSubmitting(false);
    }
  }, [isOpen]);

  const numericAmount = parseFloat(amount) || 0;
  const balanceAfter = useMemo(
    () => Math.max(0, parseFloat(currentBalance || 0) - numericAmount),
    [currentBalance, numericAmount]
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (!numericAmount || numericAmount <= 0) {
      setError("Enter a valid withdrawal amount");
      return;
    }

    if (numericAmount > parseFloat(currentBalance || 0)) {
      setError("Insufficient token reserve balance");
      return;
    }

    if (!message.trim()) {
      setError("Message is required");
      return;
    }

    try {
      setSubmitting(true);
      const result = await onWithdraw({
        amount: numericAmount,
        message: message.trim(),
      });

      if (!result?.success) {
        throw new Error(result?.error || "Failed to record withdrawal");
      }

      toast.success("Withdrawal recorded successfully");
      onClose();
    } catch (err) {
      setError(err.message || "Failed to record withdrawal");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && !submitting && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Withdraw from Token Reserve</DialogTitle>
          <DialogDescription>
            Record a withdrawal and add a note (e.g. bi-weekly salary processing summary).
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Amount
            </label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
              <Input
                type="number"
                min="0.01"
                step="0.01"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                className="pl-7"
                placeholder="0.00"
                disabled={submitting}
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Message
            </label>
            <Textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={4}
              placeholder={`The Total Toke Money from 04/27 - 05/10 was $5,282. We will use $3,392 and $1,890 will be placed in Toke Reserve.`}
              disabled={submitting}
            />
          </div>

          <div className="rounded-lg border border-yellow-200 bg-yellow-50 p-4 text-sm space-y-2">
            <div className="flex justify-between">
              <span className="text-gray-600">Current balance</span>
              <span className="font-semibold text-yellow-800">
                ${formatCurrency(currentBalance)}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Withdrawal</span>
              <span className="font-semibold text-red-600">
                -${formatCurrency(numericAmount)}
              </span>
            </div>
            <div className="border-t border-yellow-200 pt-2 flex justify-between">
              <span className="text-gray-700 font-medium">New balance</span>
              <span className="font-bold text-yellow-900">
                ${formatCurrency(balanceAfter)}
              </span>
            </div>
          </div>

          {error && (
            <p className="text-sm text-red-600">{error}</p>
          )}

          <div className="flex justify-end gap-3 pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={submitting}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={submitting}>
              {submitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                "Confirm Withdrawal"
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
